import { z } from 'zod';

const PromotionDetailsSchema = z.object({
  display_indexes: z.array(z.number()),
  cta_text: z.string().optional(),
  link: z.string(),
  button_text: z.string().optional()
});

const ExtraInfoParsedSchema = z.object({
  promotion_details: PromotionDetailsSchema.optional(),
  custom_styles: z.string().optional()
});

const ExtraInfoSchema = z
  .string()
  .transform((str) => {
    try {
      return str === 'null' || str === '' ? null : JSON.parse(str);
    } catch {
      throw new Error('Invalid JSON string');
    }
  })
  .refine(
    (val) =>
      val === null ||
      val === '' ||
      ExtraInfoParsedSchema.safeParse(val).success,
    {
      message: 'Invalid extra_info structure'
    }
  )
  .optional()
  .nullable();

const BranchExtraInfoParsedSchema = z.object({
  quota: z.string().optional(),
  stage: z.string().optional()
});

const ReservationCategoryExtraInfoSchema = z
  .string()
  .transform((str) => {
    try {
      return str === 'null' || str === '' ? null : JSON.parse(str);
    } catch {
      throw new Error('Invalid JSON string');
    }
  })
  .refine(
    (val) =>
      val === null ||
      val === '' ||
      BranchExtraInfoParsedSchema.safeParse(val).success,
    {
      message: 'Invalid extra_info structure'
    }
  )
  .optional()
  .nullable();

const ReservationCategorySchema = z.object({
  category_code: z.string().optional(),
  cutoff: z.number().positive().optional().nullable(),
  cutoff_from: z.number().positive().optional().nullable(),
  cutoff_to: z.number().positive().optional().nullable(),
  extra_info: ReservationCategoryExtraInfoSchema.optional().nullable(),
  name: z.string().optional()
});

export const GetCollegePredictorResponseSchema = z.object({
  total_no_of_colleges: z.number().int(),
  admission_category_name: z.string(),
  colleges: z.array(
    z.object({
      college_id: z.string(),
      college_name: z.string(),
      college_address: z.string(),
      district_enum: z.string().optional().nullable(),
      extra_info: ExtraInfoSchema,
      is_promoted: z.boolean(),
      branches: z.array(
        z.object({
          branch_code: z.string(),
          branch_name: z.string(),
          fee: z.number().int().optional().nullable(),
          min_fee: z.number().positive().optional().nullable(),
          max_fee: z.number().positive().optional().nullable(),
          min_placement_package: z.number().positive().optional().nullable(),
          max_placement_package: z.number().positive().optional().nullable(),
          cutoff: z.number().positive().optional().nullable(),
          reservation_categories: z
            .array(ReservationCategorySchema)
            .optional()
            .nullable()
        })
      )
    })
  )
});

export type GetCollegePredictorResponseType = z.infer<
  typeof GetCollegePredictorResponseSchema
>;

export const GetCollegePredictorFailureResponseSchema = z.object({
  response: z.string().optional(),
  http_status_code: z.number().int().optional(),
  res_status: z
    .enum([
      'INVALID_ENTRANCE_EXAM_NAME_ENUM',
      'INVALID_ADMISSION_CATEGORY_NAME_ENUM',
      'INVALID_BRANCH_CODES',
      'INVALID_RESERVATION_CATEGORY_CODE',
      'INVALID_CUTOFF_RANGE',
      'INVALID_INPUT_FORMAT'
    ])
    .optional()
});

export type GetCollegePredictorFailureResponseType = z.infer<
  typeof GetCollegePredictorFailureResponseSchema
>;

export const GetCollegePredictorRequestSchema = z.object({
  entrance_exam_name_enum: z.string(),
  admission_category_name_enum: z.string(),
  cutoff_from: z.number(),
  cutoff_to: z.number(),
  reservation_category_codes: z.array(z.string()),
  branch_codes: z.array(z.string()),
  districts: z.array(z.string()).optional(),
  sort_order: z.enum(['ASC', 'DESC']).optional()
});

export type GetCollegePredictorRequestType = z.infer<
  typeof GetCollegePredictorRequestSchema
>;
